import axios from "@/plugins/axios";

// Team Management: the signed-in brokerage or wholesale admin's own team. The API scopes every
// call to the admin's own organisation. A refusal answers success: false (or a 403/404) and the
// axios plugin shows its message once as the error toast, so an action that gets one returns it
// and changes nothing here.
const TEAM_URL = "/brokers/team";

const succeeded = (data) => data?.success === true;

// The plugin has already shown the toast for an HTTP error; the page only needs to know it failed.
const refused = (error) => ({ ...(error?.response?.data || {}), success: false, status: error?.response?.status });

const attempt = async (call) => {
  try {
    const { data } = await call();
    return data;
  } catch (error) {
    if (!error?.response) throw error;
    return refused(error);
  }
};

export default {
  namespaced: true,

  state: () => ({
    members: [],
    offices: [],
    loaded: false,
  }),

  mutations: {
    SET_TEAM(state, { members, offices }) {
      state.members = members || [];
      state.offices = offices || [];
      state.loaded = true;
    },
  },

  actions: {
    async fetchTeam({ commit }) {
      // A 403 (no longer an admin) is handled by the page, not toasted.
      const { data } = await axios.get(TEAM_URL, { withoutErrorHandler: true });
      if (succeeded(data)) commit("SET_TEAM", data);
      return data;
    },

    // role: "admin" or "contributor" (shown as "Broker"). Returns the answer: client_role is the role now held.
    async setRole({ dispatch }, { id, role }) {
      const data = await attempt(() => axios.post(`${TEAM_URL}/members/${id}/role`, { role }));
      if (!succeeded(data)) return data;
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      return data;
    },

    async addMember({ dispatch }, { email, name, localOfficeId }) {
      const data = await attempt(() =>
        axios.post(`${TEAM_URL}/members`, { email, name, local_office_id: localOfficeId || undefined })
      );
      if (!succeeded(data)) return data;
      // An invitation that could not be sent is not a success to show in green
      const messageType = data.invited === false ? "warning" : "success";
      dispatch(
        "showMessage",
        { ...data, messageType, messageTitle: messageType === "warning" ? "Note" : "Success" },
        { root: true }
      );
      return data;
    },

    async resendInvitation({ dispatch }, id) {
      const data = await attempt(() => axios.post(`${TEAM_URL}/members/${id}/invite`));
      if (!succeeded(data)) return data;
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      return data;
    },

    // { customer_count, has_submissions, submission_count, brokers: [{ entity_id, name, email }] }
    async fetchTakeover(_context, id) {
      const data = await attempt(() => axios.get(`${TEAM_URL}/members/${id}/takeover`));
      return data;
    },

    // newBrokerId: the colleague who takes the customers; none when there are no customers.
    async deactivate({ dispatch }, { id, newBrokerId }) {
      const data = await attempt(() =>
        axios.post(`${TEAM_URL}/members/${id}/deactivate`, { new_broker_id: newBrokerId || undefined })
      );
      if (!succeeded(data)) return data;
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      return data;
    },
  },
};
